<script setup lang="ts">
import { ref, computed, watch, watchEffect } from 'vue'
import { useLayersStore } from '@/stores/layers'
import { useMapsStore } from '@/stores/maps'
import { useSplitStore } from '@/stores/split'
import { useRouter } from 'vue-router'
import { unrefElement } from '@vueuse/core'

import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'

import { ChevronDown, Eye, EyeClosed, Plus, Camera, CameraOff, ClipboardPaste } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { LayerList } from '@/components/layers'
import { mapIcon } from './mapIcons'
import MapMenu from './MapMenu.vue'

const layersStore = useLayersStore()

const mapsStore = useMapsStore()

const splitStore = useSplitStore()
const router = useRouter()

const props = defineProps<{
  map: any
  orgId: number | string
}>()

const layers = computed(() => mapsStore.getMapLayers(props.orgId, props.map.id))

// The Unassigned map is derived on the client and has no backend record, so anything
// that needs a real map uuid (rename, delete, members, paste, drop-in) is unavailable.
// Its layers can still be shown on the canvas and dragged out into a real map.
const isVirtual = computed(() => !!props.map.isVirtual)

const expandedKey = computed(() => `map-expanded-${props.map.id}`)
const isExpanded = ref(localStorage.getItem(expandedKey.value) === null
  ? props.map.isDefault || !!props.map.isVirtual // default map and Unassigned start open
  : localStorage.getItem(expandedKey.value) === 'true')
watch(isExpanded, (v) => localStorage.setItem(expandedKey.value, String(v)))

const isSplitActive = computed(() => splitStore.isSplitActive(props.orgId))
const isSyncEnabled = computed(() => splitStore.isSyncEnabled(props.map.id))

// A map is visible iff it's a tile on the canvas. The eye adds/removes the tile.
const isVisibleIcon = computed(() => splitStore.isVisible(props.orgId, props.map.id))
const toggleVisibility = () => {
  splitStore.toggleMap({ orgId: props.orgId, mapId: props.map.id })
}

const toggleSync = () => {
  splitStore.toggleSync({ mapId: props.map.id })
}

const openCreateLayer = () => {
  router.push({
    name: 'LayerCreate',
    params: { org_id: props.orgId },
    query: isVirtual.value ? {} : { map_id: props.map.id }
  })
}

// Right-click the map body (incl. the empty state) to paste a copied layer here.
const clipboardLayer = computed(() => layersStore.clipboardLayer)
// A layer can only be pasted within the org it was copied from; the backend
// would reject a cross-org duplicate, so don't offer it.
const canPaste = computed(() =>
  !isVirtual.value && !!clipboardLayer.value && String(clipboardLayer.value.orgId) === String(props.orgId))
const pasteLayer = async () => {
  if (!canPaste.value) return
  try {
    await layersStore.duplicateLayerToMap({ orgId: props.orgId, layerCode: clipboardLayer.value!.layerCode, mapUuid: props.map.id })
  } catch {
    // duplicateLayerToMap already toasts on failure
  }
}

// Drop target: a layer dragged from another map and released anywhere on this
// section moves into this map (the actual move is dispatched by MapsPanel's
// monitor; here we only render the hover affordance).
const sectionRef = ref()
const isDropTarget = ref(false)

watchEffect((onCleanup) => {
  const el = unrefElement(sectionRef)
  if (!el) return
  const cleanup = combine(
    // Drop target: a layer dragged from another map lands in this one.
    dropTargetForElements({
      element: el,
      getData: () => ({ type: 'map', mapId: props.map.id }),
      canDrop: ({ source }) => !isVirtual.value && !source.data.parentUuid && !props.map.layerCodes.includes(source.data.id),
      onDragEnter: () => { isDropTarget.value = true },
      onDragLeave: () => { isDropTarget.value = false },
      onDrop: () => { isDropTarget.value = false },
      getIsSticky: () => true,
    }),
    // Drag source: drag this map card onto a split-screen tile to show it there.
    draggable({
      element: el,
      getInitialData: () => ({ type: 'mapCard', mapId: props.map.id }),
    }),
  )
  onCleanup(() => cleanup())
})
</script>

<template>
  <div
    ref="sectionRef"
    class="rounded-lg border transition-colors"
    :class="isDropTarget ? 'border-primary bg-primary/5' : 'border-transparent'"
  >
    <ContextMenu>
      <ContextMenuTrigger as-child>
        <div>
    <div
      class="flex items-center gap-1.5 px-1.5 py-1.5 rounded-md hover:bg-muted/60 group/maphdr"
    >
      <button
        type="button"
        class="flex-shrink-0 flex items-center justify-center rounded hover:bg-muted"
        @click.stop="isExpanded = !isExpanded"
      >
        <ChevronDown
          class="w-4 h-4 text-muted-foreground transition-transform duration-150"
          :class="{ '-rotate-90': !isExpanded }"
        />
      </button>
      <div class="flex items-center gap-1.5 min-w-0 flex-1 cursor-pointer" @click="isExpanded = !isExpanded">
        <span
          class="w-6 h-6 rounded-md flex items-center justify-center text-white flex-shrink-0"
          :style="{ backgroundColor: map.color }"
        >
          <component :is="mapIcon(map.icon)" class="w-3.5 h-3.5" />
        </span>
        <span class="text-sm font-semibold truncate">{{ map.name }}</span>
        <span class="text-xs text-muted-foreground flex-shrink-0">{{ layers.length }}</span>
      </div>

      <TooltipProvider :delayDuration="450">
        <div class="ml-auto flex items-center flex-shrink-0" @click.stop>
          <Tooltip>
            <TooltipTrigger as-child>
              <Button size="sm" variant="ghost" class="w-6 h-6 p-0 rounded-full" @click.stop="openCreateLayer">
                <Plus class="w-4 h-4 text-foreground" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Add layer</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip v-if="isSplitActive">
            <TooltipTrigger as-child>
              <Button size="sm" variant="ghost" class="w-6 h-6 p-0 rounded-full" @click.stop="toggleSync">
                <Camera v-if="isSyncEnabled" class="w-4 h-4 text-primary" />
                <CameraOff v-else class="w-4 h-4 text-foreground" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{{ isSyncEnabled ? 'Camera synced' : 'Camera independent' }}</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger as-child>
              <Button size="sm" variant="ghost" class="w-6 h-6 p-0 rounded-full" @click.stop="toggleVisibility">
                <Eye v-if="isVisibleIcon" class="w-4 h-4 text-primary" />
                <EyeClosed v-else class="w-4 h-4 text-foreground" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{{ isVisibleIcon ? 'Hide from canvas' : 'Show on canvas' }}</p>
            </TooltipContent>
          </Tooltip>
          <MapMenu v-if="!isVirtual" :map="map" />
        </div>
      </TooltipProvider>
    </div>

    <div v-show="isExpanded" class="pb-1">
      <!-- The Unassigned map has no per-map ordering to persist, so its reorders fall
           through to the org-wide layer sort_order. -->
      <LayerList v-if="layers.length" :layers="layers" :map-id="isVirtual ? undefined : map.id" />
      <div v-else class="px-3 py-4 text-xs text-muted-foreground">
        No layers. Drag a layer here or use Add → Layer.
      </div>
    </div>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent class="w-52">
        <ContextMenuItem v-if="!isVirtual" :disabled="!canPaste" @click="pasteLayer">
          <ClipboardPaste class="w-4 h-4 mr-2" />
          Paste layer
        </ContextMenuItem>
        <ContextMenuItem @click="openCreateLayer">
          <Plus class="w-4 h-4 mr-2" />
          Add layer
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  </div>
</template>
